import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, Gem, Coins, Sparkles, Circle, PackageX } from 'lucide-react';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import {
  formatCarats, formatGrams,
} from '@/lib/format';
// Wave 3.49 — portal-wide carat/piece validation (Rule 1 + Rule 2).
import { validateMovementQty, assertStateConsistent } from '@/lib/caratValidation';

/**
 * Return Dialog (Phase 9 redesign) — clearer step-wise picker per row:
 *   1) Pick the **Bucket** (Unattributed OR a specific Order the goldsmith holds material under)
 *   2) Pick the **Packet / Purity** that has available balance inside that bucket
 *   3) Enter the **Quantity** (carats/pieces for diamond, grams for gold) — strictly capped
 *      to the chosen packet's available balance in that bucket.
 *
 * Wave 3.48 — Optional "Return as Broken Packet" toggle per row (carat materials only):
 *   When enabled, the user enters KEPT (returned-to-broken) carats and LOSS carats
 *   separately. The total (kept + loss) comes off the goldsmith ledger; only kept
 *   ends up in a broken packet at preserved value.
 *
 * Props:
 *  - open, onOpenChange
 *  - companyId
 *  - goldsmithId (required), goldsmithName
 *  - filter (optional): {material, packetId, purityId, orderId, lineId} to preselect/narrow.
 *  - onSuccess(data): callback after successful POST /goldsmith-issues/return
 */
export default function ReturnDialog({
  open, onOpenChange,
  companyId, goldsmithId, goldsmithName,
  filter,
  onSuccess,
}) {
  const [balances, setBalances] = useState(null);
  const [reference, setReference] = useState('');
  const [headerNote, setHeaderNote] = useState('');
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // Wave 3.48 — Cache broken-packet candidate lists keyed by `${material}:${classification_id}`
  const [brokenPacketsByKey, setBrokenPacketsByKey] = useState({});

  // Reset / load balances
  useEffect(() => {
    if (!open) {
      Promise.resolve().then(() => {
        setRows([]); setReference(''); setHeaderNote(''); setBalances(null);
        setBrokenPacketsByKey({});
      });
      return;
    }
    if (!goldsmithId) return;
    let cancelled = false;
    api
      .get(`/goldsmith-issues/balance/by-goldsmith/${goldsmithId}`)
      .then(({ data }) => { if (!cancelled) setBalances(data); })
      .catch((e) => toast.error(extractError(e, 'Failed to load goldsmith balances')));
    return () => { cancelled = true; };
  }, [open, goldsmithId]);

  /**
   * Build a structured candidate map keyed by bucket and packet/purity, including all the
   * concrete lines so the user can see total available + then drill down.
   */
  const buckets = useMemo(() => {
    if (!balances) return [];
    const out = new Map();
    const ensureBucket = (key, init) => {
      if (!out.has(key)) out.set(key, { ...init, packets: new Map() });
      return out.get(key);
    };

    for (const mat of balances.by_material || []) {
      const isCarat = mat.kind === 'diamond' || mat.kind === 'color_stone' || mat.kind === 'pearl';
      if (filter?.material && filter.material !== mat.kind) continue;
      if (filter?.packetId && isCarat && filter.packetId !== mat.id) continue;
      if (filter?.purityId && mat.kind === 'gold' && filter.purityId !== mat.id) continue;
      // Unattributed bucket
      if (mat.unattributed && !filter?.orderId) {
        const bk = ensureBucket('unattributed', { key: 'unattributed', label: 'Unattributed', kind: 'unattributed' });
        const pkey = `${mat.kind}:${mat.id}`;
        if (!bk.packets.has(pkey)) bk.packets.set(pkey, {
          packet_key: pkey, kind: mat.kind, mat_id: mat.id, label: mat.label,
          classification_id: mat.classification_id,
          classification_schema: mat.classification_schema,
          total: isCarat
            ? { carats: mat.unattributed.carats || 0, pieces: mat.unattributed.pieces || 0 }
            : { grams: mat.unattributed.grams || 0 },
          lines: [],
        });
        const pkt = bk.packets.get(pkey);
        for (const ln of mat.unattributed.lines || []) {
          if (filter?.lineId && filter.lineId !== ln.line_id) continue;
          pkt.lines.push({
            line_id: ln.line_id, issue_id: ln.issue_id,
            issue_memo_no: ln.issue_memo_no, issue_no: ln.issue_no,
            sieves: ln.sieves || '', size: ln.size || '', shape: ln.shape || '',
            material_type: mat.kind, source: 'unattributed',
            available: isCarat
              ? { carats: ln.carats || 0, pieces: ln.pieces || 0 }
              : { grams: ln.grams || 0, grams_999: ln.grams_999 || 0 },
          });
        }
      }
      // Order-attributed buckets
      for (const ord of mat.orders || []) {
        // When the caller asks for the unattributed pool only (pooled
        // Return from a consolidated row), hide every order bucket.
        if (filter?.unattributedOnly) continue;
        if (filter?.orderId && filter.orderId !== ord.order_id) continue;
        const bk = ensureBucket(`order:${ord.order_id}`, {
          key: `order:${ord.order_id}`,
          label: ord.order_no || 'Order',
          kind: 'order',
          order_id: ord.order_id,
          order_no: ord.order_no,
        });
        const pkey = `${mat.kind}:${mat.id}`;
        if (!bk.packets.has(pkey)) bk.packets.set(pkey, {
          packet_key: pkey, kind: mat.kind, mat_id: mat.id, label: mat.label,
          classification_id: mat.classification_id,
          classification_schema: mat.classification_schema,
          total: isCarat ? { carats: 0, pieces: 0 } : { grams: 0 },
          lines: [],
        });
        const pkt = bk.packets.get(pkey);
        for (const ln of ord.lines || []) {
          if (filter?.lineId && filter.lineId !== ln.line_id) continue;
          if (isCarat) {
            pkt.total.carats = (pkt.total.carats || 0) + (ln.carats || 0);
            pkt.total.pieces = (pkt.total.pieces || 0) + (ln.pieces || 0);
          } else {
            pkt.total.grams = (pkt.total.grams || 0) + (ln.grams || 0);
          }
          pkt.lines.push({
            line_id: ln.line_id, issue_id: ln.issue_id,
            issue_memo_no: ln.issue_memo_no, issue_no: ln.issue_no,
            sieves: ln.sieves || '', size: ln.size || '', shape: ln.shape || '',
            material_type: mat.kind, source: ord.order_id,
            available: isCarat
              ? { carats: ln.carats || 0, pieces: ln.pieces || 0 }
              : { grams: ln.grams || 0, grams_999: ln.grams_999 || 0 },
          });
        }
      }
    }

    const buckets = Array.from(out.values()).map((b) => ({
      ...b,
      packets: Array.from(b.packets.values()).filter((p) => p.lines.length > 0),
    })).filter((b) => b.packets.length > 0);
    return buckets;
  }, [balances, filter]);

  // Auto-add a starter row when dialog opens with a strong filter
  useEffect(() => {
    if (!open) return;
    if (rows.length > 0) return;
    if (buckets.length === 0) return;
    if (filter && (filter.packetId || filter.purityId || filter.orderId || filter.lineId)) {
      const b = buckets[0];
      const p = b.packets[0];
      Promise.resolve().then(() => {
        setRows([newRow({ bucketKey: b.key, packetKey: p.packet_key })]);
      });
    }
  }, [open, buckets, filter]);

  const addRow = () => setRows((rs) => [...rs, newRow()]);
  const removeRow = (id) => setRows((rs) => rs.filter((r) => r._id !== id));
  const updateRow = (id, patch) => setRows((rs) => rs.map((r) => (r._id === id ? { ...r, ...patch } : r)));
  // Wave 4.10 — per-issue-entry allocation (diamond/colour-stone multi-entry packets).
  const updateAlloc = (id, lineId, patch) => setRows((rs) => rs.map((r) => (
    r._id === id
      ? { ...r, alloc: { ...(r.alloc || {}), [lineId]: { ...((r.alloc || {})[lineId] || {}), ...patch } } }
      : r
  )));

  // Enrich each row with the selected bucket/packet refs + the aggregated available qty.
  const enriched = rows.map((r) => {
    const bucket = buckets.find((b) => b.key === r.bucketKey) || null;
    const packet = bucket ? bucket.packets.find((p) => p.packet_key === r.packetKey) || null : null;
    const isGold = packet?.kind === 'gold';
    const availMaxCt = packet ? (packet.total.carats || 0) : 0;
    const availMaxPcs = packet ? (packet.total.pieces || 0) : 0;
    const availMaxG = packet ? (packet.total.grams || 0) : 0;
    // Wave 4.10 — Diamond / Colour-Stone packets with MORE THAN ONE issue
    // entry switch to a per-entry table so the user returns against the exact
    // sieve-wise issue entries. Single-entry packets keep the simple input.
    const supportsEntryTable = !!packet && (packet.kind === 'diamond' || packet.kind === 'color_stone');
    const entryMode = !!(supportsEntryTable && !r.toBroken && (packet.lines?.length || 0) > 1);
    let rowOver = false;
    let rowHasAny = false;
    let rowRuleViol = false;
    let rowOrphan = false;
    let entryRows = [];
    if (entryMode) {
      entryRows = (packet.lines || []).map((ln) => {
        const a = (r.alloc || {})[ln.line_id] || {};
        const ct = Number(a.carats || 0);
        const pcs = Number(a.pieces || 0);
        const availCt = ln.available?.carats || 0;
        const availPcs = ln.available?.pieces || 0;
        const over = (ct - availCt > 1e-9) || (pcs > availPcs);
        const hasQty = ct > 0 || pcs > 0;
        const ruleViol = hasQty && !!validateMovementQty(ct, pcs, { context: 'issue-entry return' });
        const remCt = availCt - ct;
        const remPcs = availPcs - pcs;
        const orphan = hasQty && !!assertStateConsistent(remCt, remPcs, { label: 'remaining on this issue entry', context: 'goldsmith return' });
        if (over) rowOver = true;
        if (hasQty) rowHasAny = true;
        if (ruleViol) rowRuleViol = true;
        if (orphan) rowOrphan = true;
        return { ...ln, ct, pcs, availCt, availPcs, over, hasQty, ruleViol, orphan };
      });
    }
    // Wave 3.48 — for to_broken rows, the "quantity" is kept + loss; the
    // standard ct/pcs inputs are hidden.
    let over = false;
    if (entryMode) {
      over = rowOver;
    } else if (packet) {
      if (r.toBroken) {
        const totalCt = Number(r.keptCt || 0) + Number(r.lossCt || 0);
        const totalPcs = Number(r.keptPcs || 0) + Number(r.lossPcs || 0);
        over = (totalCt - availMaxCt > 1e-9) || (totalPcs > availMaxPcs);
      } else if (isGold) {
        over = Number(r.grams || 0) - availMaxG > 1e-6;
      } else {
        over = (Number(r.carats || 0) - availMaxCt > 1e-9) || (Number(r.pieces || 0) > availMaxPcs);
      }
    }
    return { ...r, bucket, packet, isGold, availMaxCt, availMaxPcs, availMaxG, over,
             entryMode, entryRows, rowHasAny, rowRuleViol, rowOrphan };
  });

  // Wave 3.48 — Lazy-load broken packets for any row that has toBroken enabled.
  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    (async () => {
      for (const r of enriched) {
        if (!r.toBroken || !r.packet) continue;
        if (r.isGold) continue;
        const clsId = r.packet.classification_id;
        if (!clsId) continue;
        const key = `${r.packet.kind}:${clsId}`;
        if (brokenPacketsByKey[key]) continue;
        try {
          const apiPrefix = r.packet.kind === 'diamond'
            ? '/diamonds'
            : r.packet.kind === 'color_stone' ? '/color-stones' : '/pearls';
          const { data } = await api.get(`${apiPrefix}/packets`, {
            params: { company_id: companyId, packet_type: 'broken', classification_id: clsId },
          });
          const list = (Array.isArray(data) ? data : [])
            .filter((p) => p.packet_type === 'broken')
            .sort((a, b) => (a.packet_no || '').localeCompare(b.packet_no || ''));
          if (!cancelled) {
            setBrokenPacketsByKey((prev) => ({ ...prev, [key]: list }));
          }
        } catch (e) {
          // Soft-fail; the row error state will flag the missing list.
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, enriched.map((r) => `${r._id}:${r.toBroken ? 1 : 0}:${r.packet?.classification_id || ''}`).join('|')]);

  const anyEmpty = enriched.some((r) => !r.bucket || !r.packet);
  const anyOver = enriched.some((r) => r.over);
  const anyZero = enriched.some((r) => {
    if (!r.packet) return true;
    // Wave 4.10 — per-entry table: at least one entry must carry a valid
    // both-sided quantity, and every filled entry must satisfy Rule 1.
    if (r.entryMode) return !r.rowHasAny || r.rowRuleViol;
    if (r.toBroken) {
      // Wave 3.49 Rule 1 — KEPT side of a goldsmith→broken return must
      // satisfy ct > 0 AND pcs > 0. Loss may be 0/0 (no piece lost) or
      // (X ct, 0 pcs) (weight loss without piece loss — exception).
      return !!validateMovementQty(r.keptCt, r.keptPcs, { context: 'kept (returned-to-broken)' });
    }
    if (r.isGold) return !(Number(r.grams || 0) > 0);
    // Wave 3.49 Rule 1 — standard returns require both ct > 0 AND pcs > 0.
    return !!validateMovementQty(r.carats, r.pieces, { context: 'standard goldsmith return' });
  });
  // Wave 3.49 Rule 2 — for non-gold carat returns, the source bucket's
  // post-return attribution must satisfy ((carats > 0) IFF (pieces > 0)).
  // Skips ``toBroken`` rows because the kept-side validation already
  // forces both qty > 0 and Rule 2 is enforced on the underlying
  // send-to-broken step on the backend (with full kept+loss accounted).
  const anyOrphanResidue = enriched.some((r) => {
    if (r.entryMode) return r.rowOrphan;
    if (!r.packet || r.toBroken || r.isGold) return false;
    const ct = Number(r.carats || 0);
    const pcs = Number(r.pieces || 0);
    if (ct <= 0 && pcs <= 0) return false; // already covered by anyZero
    const remCt = (r.availMaxCt || 0) - ct;
    const remPcs = (r.availMaxPcs || 0) - pcs;
    return !!assertStateConsistent(remCt, remPcs, { label: 'remaining goldsmith-held qty', context: 'goldsmith return' });
  });
  const anyBrokenMisconfigured = enriched.some((r) => {
    if (!r.toBroken || !r.packet) return false;
    if (r.isGold) return true; // shouldn't happen — UI hides it for gold
    if (r.brokenMode === 'existing' && !r.brokenPacketId) return true;
    return false;
  });
  const canSubmit = rows.length > 0 && !anyEmpty && !anyOver && !anyZero && !anyOrphanResidue && !anyBrokenMisconfigured && !submitting;

  const submit = async () => {
    setSubmitting(true);
    let result = null;
    try {
      const lines = [];
      // Wave 3.52 — Packet-level returns. We send ONE line per (bucket,
      // packet) carrying the TOTAL quantity; the backend distributes it
      // FIFO across the goldsmith's issue memos. The old client-side FIFO
      // allocator was removed: it emitted milli-carat slices that the
      // backend's 2-dp round_carats floored to 0.00, creating rejected
      // single-sided "N pcs / 0.00 ct" fragments while earlier slices were
      // already committed (ledger updated + error toast).
      for (const r of enriched) {
        if (!r.bucket || !r.packet) continue;
        const source = r.bucket.kind === 'unattributed' ? 'unattributed' : r.bucket.order_id;
        // Wave 4.10 — Per-issue-entry returns (diamond/colour stone, multi-entry
        // packets). Emit ONE line-level return per issue entry that carries a
        // quantity, targeting the exact (issue_id, line_id) so the return is
        // tracked sieve-wise against the specific issue — NOT FIFO-distributed.
        if (r.entryMode) {
          for (const ln of (r.packet.lines || [])) {
            const a = (r.alloc || {})[ln.line_id] || {};
            const ct = Number(a.carats || 0);
            const pcs = Number(a.pieces || 0);
            if (ct <= 0 && pcs <= 0) continue;
            lines.push({
              issue_id: ln.issue_id,
              line_id: ln.line_id,
              source,
              carats: ct,
              pieces: pcs,
              note: r.note || '',
            });
          }
          continue;
        }
        const lineBody = {
          material_type: r.packet.kind,
          source,
          note: r.note || '',
        };
        if (r.isGold) {
          lineBody.purity_id = r.packet.mat_id;
          lineBody.grams = Number(r.grams || 0);
        } else if (r.toBroken) {
          // Broken-packet salvage. Carats/pieces carry the kept+loss total;
          // the to_broken spec drives the send-to-broken transfer on the
          // backend (which operates on the packet as a whole).
          lineBody.packet_id = r.packet.mat_id;
          lineBody.carats = Number(r.keptCt || 0) + Number(r.lossCt || 0);
          lineBody.pieces = Number(r.keptPcs || 0) + Number(r.lossPcs || 0);
          lineBody.to_broken = {
            enabled: true,
            kept_carats: Number(r.keptCt || 0),
            kept_pieces: Number(r.keptPcs || 0),
            loss_carats: Number(r.lossCt || 0),
            loss_pieces: Number(r.lossPcs || 0),
            target_mode: r.brokenMode || 'existing',
            target_broken_packet_id: r.brokenMode === 'existing' ? (r.brokenPacketId || null) : null,
            new_broken_packet: r.brokenMode === 'new' ? {
              classification_id: r.packet.classification_id,
              description: (r.newBrokenDescription || '').trim() || undefined,
            } : null,
          };
        } else {
          lineBody.packet_id = r.packet.mat_id;
          lineBody.carats = Number(r.carats || 0);
          lineBody.pieces = Number(r.pieces || 0);
        }
        lines.push(lineBody);
      }
      if (lines.length === 0) {
        toast.error('Nothing to return — please enter a valid quantity.');
        setSubmitting(false); return;
      }
      const payload = { goldsmith_id: goldsmithId, company_id: companyId, reference, note: headerNote, lines };
      const { data } = await api.post('/goldsmith-issues/return', payload);
      result = data;
    } catch (e) {
      // Only a genuine POST failure lands here. Because the backend now
      // validates & commits the whole return atomically, a 400/409 means
      // NOTHING was written — so this error toast never contradicts the
      // ledger.
      toast.error(extractError(e, 'Return failed'));
      setSubmitting(false);
      return;
    }
    // POST succeeded. Surface success + refresh; a refresh error must NOT
    // masquerade as a return failure.
    const hasBroken = !!result?.has_broken_packet_return;
    toast.success(`Return ${result.return_memo_no || result.return_no} created${hasBroken ? ' · broken-packet voucher available' : ''}`);
    setSubmitting(false);
    onOpenChange(false);
    try { onSuccess && onSuccess(result); } catch (_e) { /* ignore refresh errors */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Return Material{goldsmithName ? ` from ${goldsmithName}` : ''}</DialogTitle>
          <DialogDescription>
            Pick a bucket (Unattributed or a specific Order), choose the packet/purity that has
            available balance, then enter the quantity to return. Quantities are capped to the
            available balance for that bucket+packet pair. For broken stones, toggle &ldquo;Return as
            Broken Packet&rdquo; to route the kept material into a salvage packet with weight loss.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <Label>Reference (optional)</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Return slip / chit no" data-testid="return-reference-input" />
          </div>
          <div>
            <Label>Header Note (optional)</Label>
            <Input value={headerNote} onChange={(e) => setHeaderNote(e.target.value)} placeholder="Applies to all rows" data-testid="return-note-input" />
          </div>
        </div>

        {!balances && (
          <div className="text-sm text-slate-500 py-6 text-center">Loading goldsmith balances…</div>
        )}
        {balances && buckets.length === 0 && (
          <div className="text-sm text-slate-500 py-6 text-center border border-dashed rounded-md">
            No returnable balances{filter ? ' match this context.' : ' for this goldsmith.'}
          </div>
        )}

        {balances && buckets.length > 0 && (
          <>
            <div className="space-y-2 max-h-[55vh] overflow-y-auto" data-testid="return-rows">
              {enriched.map((row, idx) => {
                const brokenKey = (!row.isGold && row.packet?.classification_id)
                  ? `${row.packet.kind}:${row.packet.classification_id}` : null;
                const brokenList = brokenKey ? (brokenPacketsByKey[brokenKey] || []) : [];
                return (
                  <Card key={row._id} className="p-3" data-testid={`return-row-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs text-slate-500 font-medium">Row {idx + 1}</div>
                      <button
                        onClick={() => removeRow(row._id)}
                        className="text-xs text-rose-600 hover:underline inline-flex items-center"
                        data-testid={`return-row-remove-${idx}`}
                      >
                        <Trash2 size={11} className="mr-1" /> Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-12 gap-2">
                      {/* Step 1 — Bucket */}
                      <div className="col-span-12 md:col-span-3">
                        <Label className="text-[11px]">1. Bucket</Label>
                        <SearchableSelect
                          items={buckets}
                          value={row.bucketKey || ''}
                          onValueChange={(v) => updateRow(row._id, { bucketKey: v || '', packetKey: '', carats: '', pieces: '', grams: '', alloc: {}, toBroken: false, keptCt: '', keptPcs: '', lossCt: '', lossPcs: '' })}
                          itemToValue={(b) => b.key}
                          itemToLabel={(b) => (b.kind === 'unattributed' ? 'Unattributed' : `Order ${b.label}`)}
                          searchKeys={['label', 'kind']}
                          placeholder="Pick bucket"
                          searchPlaceholder="Search bucket…"
                          testId={`return-row-bucket-${idx}`}
                        />
                      </div>

                      {/* Step 2 — Packet / Purity */}
                      <div className="col-span-12 md:col-span-4">
                        <Label className="text-[11px]">2. Packet / Purity</Label>
                        {(() => {
                          const pktItems = row.bucket?.packets || [];
                          return (
                            <SearchableSelect
                              items={pktItems}
                              value={row.packetKey || ''}
                              onValueChange={(v) => updateRow(row._id, { packetKey: v || '', carats: '', pieces: '', grams: '', alloc: {}, toBroken: false, keptCt: '', keptPcs: '', lossCt: '', lossPcs: '' })}
                              itemToValue={(p) => p.packet_key}
                              itemToLabel={(p) => {
                                const isCarat = p.kind === 'diamond' || p.kind === 'color_stone' || p.kind === 'pearl';
                                const tail = isCarat
                                  ? `${formatCarats(p.total.carats || 0)} ct / ${p.total.pieces || 0} pcs avail`
                                  : `${formatGrams(p.total.grams || 0)} g avail`;
                                const cls = p.classification_schema ? ` · ${p.classification_schema}` : '';
                                return `${p.label}${cls} — ${tail}`;
                              }}
                              searchKeys={['label', 'classification_schema', 'kind']}
                              placeholder={row.bucket ? 'Pick packet/purity' : 'Pick bucket first'}
                              searchPlaceholder="Search packet…"
                              disabled={!row.bucket}
                              testId={`return-row-packet-${idx}`}
                            />
                          );
                        })()}
                      </div>

                      {/* Step 3 — Quantity (standard) OR (broken split) */}
                      {row.toBroken && !row.isGold && row.packet ? (
                        <>
                          <div className="col-span-6 md:col-span-2">
                            <Label className="text-[11px] text-amber-700">Kept ct → Broken</Label>
                            <Input
                              className="h-9" type="number" step="0.001" value={row.keptCt || ''}
                              onChange={(e) => updateRow(row._id, { keptCt: e.target.value })}
                              placeholder="e.g. 0.09"
                              data-testid={`return-row-kept-ct-${idx}`}
                            />
                          </div>
                          <div className="col-span-6 md:col-span-1">
                            <Label className="text-[11px] text-amber-700">Kept pcs</Label>
                            <Input
                              className="h-9" type="number" step="1" value={row.keptPcs || ''}
                              onChange={(e) => updateRow(row._id, { keptPcs: e.target.value })}
                              placeholder="0"
                              data-testid={`return-row-kept-pcs-${idx}`}
                            />
                          </div>
                          <div className="col-span-6 md:col-span-1">
                            <Label className="text-[11px] text-rose-700">Loss ct</Label>
                            <Input
                              className="h-9" type="number" step="0.001" value={row.lossCt || ''}
                              onChange={(e) => updateRow(row._id, { lossCt: e.target.value })}
                              placeholder="e.g. 0.01"
                              data-testid={`return-row-loss-ct-${idx}`}
                            />
                          </div>
                          <div className="col-span-6 md:col-span-1">
                            <Label className="text-[11px] text-rose-700">Loss pcs</Label>
                            <Input
                              className="h-9" type="number" step="1" value={row.lossPcs || ''}
                              onChange={(e) => updateRow(row._id, { lossPcs: e.target.value })}
                              placeholder="0"
                              data-testid={`return-row-loss-pcs-${idx}`}
                            />
                          </div>
                        </>
                      ) : row.isGold ? (
                        <div className="col-span-12 md:col-span-3">
                          <Label className="text-[11px]">3. Grams (max {formatGrams(row.availMaxG || 0)})</Label>
                          <Input
                            className="h-9" type="number" step="0.001" value={row.grams}
                            onChange={(e) => updateRow(row._id, { grams: e.target.value })}
                            disabled={!row.packet}
                            data-testid={`return-row-grams-${idx}`}
                            placeholder={`Max ${formatGrams(row.availMaxG || 0)}`}
                          />
                        </div>
                      ) : row.entryMode ? (
                        <div className="col-span-12 md:col-span-5 self-end pb-2 text-[11px] font-medium text-indigo-700" data-testid={`return-row-entrytable-hint-${idx}`}>
                          Multiple issue entries in this packet — enter the return quantity against each entry below ↓
                        </div>
                      ) : row.packet ? (
                        <>
                          <div className="col-span-6 md:col-span-2">
                            <Label className="text-[11px]">3a. Carats (max {formatCarats(row.availMaxCt || 0)})</Label>
                            <Input
                              className="h-9" type="number" step="0.01" value={row.carats}
                              onChange={(e) => updateRow(row._id, { carats: e.target.value })}
                              data-testid={`return-row-carats-${idx}`}
                              placeholder={`${formatCarats(row.availMaxCt || 0)}`}
                            />
                          </div>
                          <div className="col-span-6 md:col-span-1">
                            <Label className="text-[11px]">3b. Pcs (max {row.availMaxPcs || 0})</Label>
                            <Input
                              className="h-9" type="number" step="1" value={row.pieces}
                              onChange={(e) => updateRow(row._id, { pieces: e.target.value })}
                              data-testid={`return-row-pieces-${idx}`}
                              placeholder={`${row.availMaxPcs || 0}`}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="col-span-3 text-xs text-slate-400 self-end pb-2">Pick packet first</div>
                      )}

                      <div className="col-span-12 md:col-span-2">
                        <Label className="text-[11px]">Note</Label>
                        <Input
                          className="h-9" value={row.note || ''}
                          onChange={(e) => updateRow(row._id, { note: e.target.value })}
                          data-testid={`return-row-note-${idx}`}
                        />
                      </div>
                    </div>

                    {/* Wave 4.10 — Per-issue-entry return table (diamond /
                        colour stone packets with multiple issue entries). The
                        user returns against the exact sieve-wise issue entries. */}
                    {row.entryMode && row.packet && (
                      <div className="mt-3 rounded-md border border-indigo-200 bg-indigo-50/30 p-2.5" data-testid={`return-row-entrytable-${idx}`}>
                        <div className="mb-2 flex items-center gap-2 text-[12px] font-medium text-indigo-900">
                          <Gem size={13} className="text-indigo-700" />
                          Issue entries for {row.packet.label}
                          <Badge variant="outline" className="text-[10px] text-indigo-700 border-indigo-300">{row.entryRows.length} entries</Badge>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-[11px]">
                            <thead>
                              <tr className="text-left text-slate-500">
                                <th className="px-2 py-1 font-medium whitespace-nowrap">Issue Memo</th>
                                <th className="px-2 py-1 font-medium">Particulars (Shape · Size · Sieves)</th>
                                <th className="px-2 py-1 font-medium text-right whitespace-nowrap">Available</th>
                                <th className="px-2 py-1 font-medium text-right">Return ct</th>
                                <th className="px-2 py-1 font-medium text-right">Return pcs</th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.entryRows.map((ln, li) => (
                                <tr key={ln.line_id} className={`border-t border-indigo-100 ${ln.over ? 'bg-rose-50/60' : ''}`} data-testid={`return-entry-${idx}-${li}`}>
                                  <td className="px-2 py-1 font-mono text-slate-700 whitespace-nowrap align-top">{ln.issue_memo_no || ln.issue_no || '—'}</td>
                                  <td className="px-2 py-1 align-top">
                                    {(ln.shape || ln.size || ln.sieves) ? (
                                      <div className="flex flex-wrap gap-1">
                                        {ln.shape ? <span className="rounded border border-indigo-200 bg-white px-1.5 py-0.5 text-[10px] text-indigo-700">Shape: {ln.shape}</span> : null}
                                        {ln.size ? <span className="rounded border border-indigo-200 bg-white px-1.5 py-0.5 text-[10px] text-indigo-700">Size: {ln.size}</span> : null}
                                        {ln.sieves ? <span className="rounded border border-indigo-200 bg-white px-1.5 py-0.5 text-[10px] text-indigo-700">Sieves: {ln.sieves}</span> : null}
                                      </div>
                                    ) : <span className="text-slate-400">—</span>}
                                  </td>
                                  <td className="px-2 py-1 text-right font-mono text-slate-600 whitespace-nowrap align-top">{formatCarats(ln.availCt)} ct · {ln.availPcs} pcs</td>
                                  <td className="px-2 py-1 text-right align-top">
                                    <Input
                                      className="h-8 w-20 ml-auto text-right" type="number" step="0.01"
                                      value={(row.alloc?.[ln.line_id]?.carats) ?? ''}
                                      onChange={(e) => updateAlloc(row._id, ln.line_id, { carats: e.target.value })}
                                      placeholder={`${formatCarats(ln.availCt)}`}
                                      data-testid={`return-entry-carats-${idx}-${li}`}
                                    />
                                  </td>
                                  <td className="px-2 py-1 text-right align-top">
                                    <Input
                                      className="h-8 w-16 ml-auto text-right" type="number" step="1"
                                      value={(row.alloc?.[ln.line_id]?.pieces) ?? ''}
                                      onChange={(e) => updateAlloc(row._id, ln.line_id, { pieces: e.target.value })}
                                      placeholder={`${ln.availPcs}`}
                                      data-testid={`return-entry-pieces-${idx}-${li}`}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {row.rowRuleViol && (
                          <div className="mt-1 text-[11px] text-rose-600">Each entry you return must have both carats &gt; 0 and pieces &gt; 0.</div>
                        )}
                      </div>
                    )}

                    {/* Wave 3.48 — Return as Broken Packet toggle (carat materials only) */}
                    {row.packet && !row.isGold && !row.entryMode && (
                      <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/40 p-2.5">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            <PackageX size={13} className="text-amber-700" />
                            <span className="text-[12px] font-medium text-amber-900">Return as Broken Packet</span>
                            <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">
                              Salvage flow
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-[11px] text-slate-600">Off</Label>
                            <Switch
                              checked={!!row.toBroken}
                              onCheckedChange={(v) => updateRow(row._id, {
                                toBroken: v,
                                carats: '', pieces: '',
                                keptCt: '', keptPcs: '', lossCt: '', lossPcs: '',
                                brokenMode: 'existing',
                                brokenPacketId: '',
                                newBrokenDescription: '',
                              })}
                              data-testid={`return-row-to-broken-toggle-${idx}`}
                            />
                            <Label className="text-[11px] text-slate-600">On</Label>
                          </div>
                        </div>
                        {row.toBroken && (
                          <div className="mt-2.5 space-y-2">
                            <div className="text-[11px] text-amber-900 leading-relaxed">
                              Total off-goldsmith: <span className="font-mono font-semibold">{(Number(row.keptCt || 0) + Number(row.lossCt || 0)).toFixed(3)} ct · {Number(row.keptPcs || 0) + Number(row.lossPcs || 0)} pcs</span>
                              {' '}— available: <span className="font-mono">{formatCarats(row.availMaxCt || 0)} ct · {row.availMaxPcs || 0} pcs</span>
                            </div>
                            <Tabs
                              value={row.brokenMode || 'existing'}
                              onValueChange={(v) => updateRow(row._id, { brokenMode: v, brokenPacketId: '' })}
                            >
                              <TabsList className="grid grid-cols-2 w-full max-w-md h-8">
                                <TabsTrigger value="existing" className="text-[11px]" data-testid={`return-row-broken-mode-existing-${idx}`}>
                                  Existing broken packet
                                </TabsTrigger>
                                <TabsTrigger value="new" className="text-[11px]" data-testid={`return-row-broken-mode-new-${idx}`}>
                                  Create new
                                </TabsTrigger>
                              </TabsList>
                              <TabsContent value="existing" className="mt-2">
                                {brokenList.length === 0 ? (
                                  <div className="text-[11px] text-slate-500 italic text-center py-2">
                                    No matching broken packets — switch to &ldquo;Create new&rdquo; to spin one up.
                                  </div>
                                ) : (
                                  <div className="max-h-32 overflow-y-auto border border-amber-200 rounded-md bg-white" data-testid={`return-row-broken-list-${idx}`}>
                                    {brokenList.map((p) => {
                                      const sel = p.id === row.brokenPacketId;
                                      return (
                                        <button
                                          key={p.id}
                                          type="button"
                                          onClick={() => updateRow(row._id, { brokenPacketId: p.id })}
                                          className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left text-xs hover:bg-amber-50 transition-colors border-b border-amber-100 last:border-b-0 ${sel ? 'bg-amber-100/70' : ''}`}
                                          data-testid={`return-row-broken-option-${idx}-${p.id}`}
                                        >
                                          <div className="min-w-0">
                                            <div className="font-mono font-medium text-amber-900 truncate">
                                              {p.packet_no} <span className="text-amber-600">·BR</span>
                                            </div>
                                            <div className="text-[10px] text-amber-700/80 truncate">
                                              {formatCarats(p.balance_carats || 0)} ct · {p.balance_pieces || 0} pcs
                                            </div>
                                          </div>
                                          {sel && <Badge className="bg-amber-600 text-white text-[10px]">Selected</Badge>}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </TabsContent>
                              <TabsContent value="new" className="mt-2 space-y-2">
                                <div className="text-[11px] text-slate-600">
                                  A new broken packet will be created with classification{' '}
                                  <span className="font-medium text-amber-900">{row.packet.classification_schema}</span>{' '}
                                  and a fresh <span className="font-mono">-BR</span> packet number.
                                </div>
                                <Input
                                  className="h-9 text-xs"
                                  value={row.newBrokenDescription || ''}
                                  onChange={(e) => updateRow(row._id, { newBrokenDescription: e.target.value })}
                                  placeholder={`e.g. Goldsmith salvage from ${row.packet.label}`}
                                  data-testid={`return-row-new-broken-description-${idx}`}
                                />
                              </TabsContent>
                            </Tabs>
                          </div>
                        )}
                      </div>
                    )}

                    {row.packet && (
                      <div className="mt-2 text-[11px] flex items-center gap-2 flex-wrap">
                        {(() => {
                          const k = row.packet.kind;
                          const Icon = k === 'color_stone' ? Sparkles : k === 'pearl' ? Circle : k === 'gold' ? Coins : Gem;
                          const color = k === 'color_stone' ? 'text-[var(--colorstone-icon,#C84A6F)]'
                                      : k === 'pearl' ? 'text-[var(--pearl-icon,#6B7280)]'
                                      : k === 'gold' ? 'text-[var(--gold-icon)]'
                                      : 'text-[var(--diamond-icon)]';
                          const label = k === 'color_stone' ? 'Colour Stone' : k === 'pearl' ? 'Pearl' : k === 'gold' ? 'Gold' : 'Diamond';
                          return (
                            <>
                              <Icon size={11} className={color} />
                              <Badge variant="secondary" className="text-[10px]">{label}</Badge>
                            </>
                          );
                        })()}
                        <span className="text-slate-500">Available in this bucket:</span>
                        <span className="font-mono text-slate-700">
                          {row.packet.kind === 'gold'
                            ? `${formatGrams(row.availMaxG)} g`
                            : `${formatCarats(row.availMaxCt)} ct · ${row.availMaxPcs} pcs`}
                        </span>
                        <span className="text-slate-500">
                          {row.entryMode
                            ? `across ${row.packet.lines.length} issue entries (choose per entry).`
                            : `across ${row.packet.lines.length} line(s) (FIFO).`}
                        </span>
                        {row.over && <Badge className="bg-rose-50 text-rose-700">Exceeds available</Badge>}
                        {row.toBroken && (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]" data-testid={`return-row-to-broken-badge-${idx}`}>
                            <PackageX size={9} className="mr-1" /> Goldsmith → Broken
                          </Badge>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>

            <div className="flex items-center justify-between mt-3">
              <Button variant="outline" onClick={addRow} data-testid="return-add-row">
                <Plus size={14} className="mr-1.5" /> Add Row
              </Button>
              <div className="text-sm text-slate-600">
                {rows.length} row(s){anyOver ? ' · check balances' : ''}
              </div>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="luxury-button"
            data-testid="return-submit-button"
          >
            {submitting ? 'Returning…' : 'Confirm Return'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function newRow(extra = {}) {
  return {
    _id: Math.random(),
    bucketKey: '',
    packetKey: '',
    carats: '',
    pieces: '',
    grams: '',
    note: '',
    // Wave 4.10 — per-issue-entry allocations: { [line_id]: {carats, pieces} }
    alloc: {},
    // Wave 3.48 — broken packet routing per-row
    toBroken: false,
    keptCt: '',
    keptPcs: '',
    lossCt: '',
    lossPcs: '',
    brokenMode: 'existing',
    brokenPacketId: '',
    newBrokenDescription: '',
    ...extra,
  };
}
